import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Alert,
  Card,
  CardContent,
  Chip,
  Grid,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Footer from '../../componentsPeopletech/Footer/Footer';

const BASE_PATH = '/peoplefortech/v2';
const API_URL = process.env.REACT_APP_API_URL || 'https://peoplefortech.com/api/v1';
const SITE_URL = 'https://www.peoplefortech.com';
const CANONICAL_PATH = `${BASE_PATH}/reserva`;

const DISCOUNT_CODE = 'CEI2026';
const PRICE_EUR = 15;

const getNextWeeks = (weeks = 4) => {
  const weeks_arr = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let w = 0; w < weeks; w++) {
    const start = new Date(today);
    start.setDate(today.getDate() + w * 7);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    weeks_arr.push({ start, end, days: [] });
    for (let d = 0; d < 7; d++) {
      const day = new Date(start);
      day.setDate(start.getDate() + d);
      weeks_arr[w].days.push(day);
    }
  }
  return weeks_arr;
};

const getSlotsForDay = (date) => {
  const day = date.getDay();
  const dateStr = date.toISOString().slice(0, 10);
  const slots = [];
  if (day >= 1 && day <= 4) {
    ['13:00', '13:30', '17:00', '17:30'].forEach((time) => {
      slots.push({
        key: `${dateStr} ${time}`,
        label: `${time} - ${time === '13:00' ? '13:20' : time === '13:30' ? '13:50' : time === '17:00' ? '17:20' : '17:50'}`,
      });
    });
  } else if (day === 5) {
    ['09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '12:00', '12:30'].forEach((time) => {
      const end = time === '12:30' ? '12:50' : `${time.slice(0, 2)}:${parseInt(time.slice(3), 10) + 20}`;
      slots.push({
        key: `${dateStr} ${time}`,
        label: `${time} - ${end}`,
      });
    });
  }
  return slots;
};

const getDayNames = (t) => (t('v2.booking.daysShort') || 'Sun,Mon,Tue,Wed,Thu,Fri,Sat').split(',');

const SLOTS_PER_PAGE = 8; // 2 filas × 4 columnas

const BookingPage = () => {
  const [weeks, setWeeks] = useState([]);
  const [availableSlots, setAvailableSlots] = useState(new Set());
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [email, setEmail] = useState('');
  const [discountCode, setDiscountCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [slotPage, setSlotPage] = useState(1);
  const { t } = useTranslation();

  const allSlotItems = useMemo(() => {
    return weeks.flatMap((week) =>
      week.days
        .filter((d) => d.getDay() >= 1 && d.getDay() <= 5)
        .flatMap((day) =>
          getSlotsForDay(day).map((slot) => ({ day, slot }))
        )
    );
  }, [weeks]);

  const totalSlotPages = Math.max(1, Math.ceil(allSlotItems.length / SLOTS_PER_PAGE));
  const visibleSlotItems = allSlotItems.slice(
    (slotPage - 1) * SLOTS_PER_PAGE,
    slotPage * SLOTS_PER_PAGE
  );

  const price = discountCode.toUpperCase().trim() === DISCOUNT_CODE ? 0 : PRICE_EUR;

  useEffect(() => {
    setWeeks(getNextWeeks());
  }, []);

  useEffect(() => {
    const fetchSlots = async () => {
      try {
        const res = await fetch(`${API_URL}/bookings/slots`);
        const data = await res.json();
        if (data.success && data.data) {
          const taken = new Set(data.data.taken || []);
          setAvailableSlots(taken);
        }
      } catch (e) {
        console.error(e);
        setAvailableSlots(new Set());
      } finally {
        setLoading(false);
      }
    };
    fetchSlots();
  }, []);

  const isSlotAvailable = (slotKey) => !availableSlots.has(slotKey);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !selectedSlot) {
      setMessage({ type: 'error', text: t('v2.booking.errorEmailSlot') });
      return;
    }
    setSubmitting(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await fetch(`${API_URL}/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          slot_key: selectedSlot.key,
          slot_label: selectedSlot.label,
          discount_code: discountCode || undefined,
          price,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setBookingSuccess(true);
        setEmail('');
        setSelectedSlot(null);
        setDiscountCode('');
        setAvailableSlots((prev) => new Set([...prev, data.data?.slot_key].filter(Boolean)));
      } else {
        setMessage({ type: 'error', text: data.error || t('v2.booking.errorGeneric') });
      }
    } catch (err) {
      setMessage({ type: 'error', text: t('v2.booking.errorConnection') });
    } finally {
      setSubmitting(false);
    }
  };

  if (bookingSuccess) {
    return (
      <>
        <Helmet>
          <title>{t('v2.booking.pageTitleSuccess')}</title>
          <meta name="robots" content="noindex,follow" />
          <link rel="canonical" href={`${SITE_URL}${CANONICAL_PATH}`} />
        </Helmet>
        <Container sx={{ py: 8, minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box sx={{ textAlign: 'center', maxWidth: 480 }}>
            <CheckCircleIcon sx={{ fontSize: 120, color: '#2e7d32', mb: 3 }} />
            <Typography variant="h4" fontWeight={700} sx={{ color: '#2e7d32', mb: 2 }}>
              {t('v2.booking.successMessage')}
            </Typography>
            <Button component={Link} to={BASE_PATH} variant="contained" sx={{ mt: 3, backgroundColor: '#000', '&:hover': { backgroundColor: '#333' } }}>
              {t('v2.booking.backHome')}
            </Button>
          </Box>
        </Container>
        <Footer />
      </>
    );
  }

  const dayNames = getDayNames(t);

  return (
    <>
      <Helmet>
        <title>{t('v2.booking.pageTitle')}</title>
        <meta name="description" content={t('v2.booking.pageDescription')} />
        <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1" />
        <link rel="canonical" href={`${SITE_URL}${CANONICAL_PATH}`} />
      </Helmet>
      <Container sx={{ py: 6, minHeight: '80vh' }}>
        <Typography variant="h4" fontWeight={700} align="center" gutterBottom>
          {t('v2.booking.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
          {t('v2.booking.subtitle')}
        </Typography>

        {message.text && (
          <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage({ type: '', text: '' })}>
            {message.text}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>{t('v2.booking.availableSlotsTitle')}</Typography>
              {loading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <Grid container spacing={2}>
                    {visibleSlotItems.map(({ day, slot }) => {
                      const available = isSlotAvailable(slot.key);
                      const selected = selectedSlot?.key === slot.key;
                      return (
                        <Grid item xs={6} sm={4} md={3} key={slot.key}>
                          <Chip
                            label={`${dayNames[day.getDay()]} ${day.getDate()}/${day.getMonth() + 1} ${slot.label}`}
                            onClick={() => available && setSelectedSlot(slot)}
                            color={selected ? 'primary' : 'default'}
                            variant={selected ? 'filled' : 'outlined'}
                            disabled={!available}
                            sx={{ width: '100%', justifyContent: 'center' }}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                  {allSlotItems.length > SLOTS_PER_PAGE && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={slotPage <= 1}
                        onClick={() => setSlotPage((p) => Math.max(1, p - 1))}
                      >
                        {t('v2.booking.paginationPrevious')}
                      </Button>
                      <Typography variant="body2" color="text.secondary">
                        {t('v2.booking.paginationPageOf', { current: slotPage, total: totalSlotPages })}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={slotPage >= totalSlotPages}
                        onClick={() => setSlotPage((p) => Math.min(totalSlotPages, p + 1))}
                      >
                        {t('v2.booking.paginationNext')}
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <TextField
                fullWidth
                label={t('v2.booking.emailLabel')}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('v2.booking.emailPlaceholder')}
                required
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label={t('v2.booking.discountCodeLabel')}
                value={discountCode}
                onChange={(e) => setDiscountCode(e.target.value)}
                placeholder=""
                sx={{ mb: 2 }}
              />
              <Typography variant="h6" color="primary" gutterBottom>
                {t('v2.booking.totalLabel')} {price === 0 ? t('v2.booking.totalFree') : `${price}€`}
              </Typography>
            </CardContent>
          </Card>

          <Box display="flex" gap={2} flexWrap="wrap">
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={submitting || !selectedSlot || !email}
              sx={{ backgroundColor: '#000', '&:hover': { backgroundColor: '#333' } }}
            >
              {submitting ? t('v2.booking.submitting') : t('v2.booking.submitButton')}
            </Button>
            <Button component={Link} to={BASE_PATH} variant="outlined">
              {t('v2.booking.backHome')}
            </Button>
          </Box>
        </Box>
      </Container>
      <Footer />
    </>
  );
};

export default BookingPage;
